/**
 * Guard → Governance Feedback Bridge — P-9 (Audit Verbeterplan v4.1)
 *
 * Reads guard rejection telemetry and produces governance-compatible
 * issue summaries. This closes the feedback loop: questions that are
 * repeatedly rejected at runtime become visible to content authors
 * in the governance dashboard.
 *
 * Usage:
 *   import { getGuardGovernanceIssues } from './guardGovernanceBridge'
 *   const issues = getGuardGovernanceIssues()
 *   // → array of questions with rejection counts, sorted by frequency
 */

import type { GuardRejection } from './pedagogicalGuard'
import { getGuardTelemetry } from './guardTelemetry'

// ─── Types ────────────────────────────────────────────────────────────────────

export interface GuardGovernanceIssue {
  /** Question ID that was rejected */
  questionId: string
  /** Family the question belongs to */
  familyId: string
  /** Number of times this question was rejected */
  rejectionCount: number
  /** Number of fatal rejections (question was removed from session) */
  fatalCount: number
  /** Number of soft rejections (question was allowed but flagged) */
  softCount: number
  /** Rejection reasons with counts */
  reasons: Record<string, number>
  /** Primary (most frequent) rejection reason */
  primaryReason: string
  /** First seen timestamp */
  firstSeen: string
  /** Last seen timestamp */
  lastSeen: string
}

export interface GuardGovernanceSummary {
  /** Total unique questions with rejections */
  totalAffectedQuestions: number
  /** Total rejection events across all questions */
  totalRejections: number
  /** Questions sorted by rejection frequency (most rejected first) */
  issues: GuardGovernanceIssue[]
  /** Breakdown by rejection reason */
  reasonBreakdown: { reason: string; count: number; description: string }[]
  /** Data freshness: timestamp range of telemetry data */
  dataRange: { from: string; to: string } | null
}

// ─── Reason Descriptions ──────────────────────────────────────────────────────

const REASON_DESCRIPTIONS: Record<string, string> = {
  'tier-dok-mismatch':
    'Vraag heeft te laag DOK-niveau voor de tier (bijv. recall-vraag op expert-tier)',
  'indicator-trivial':
    'Visuele indicator lekt het antwoord (indicator waarde ≈ correct antwoord)',
  'distractors-untagged':
    'MC-vraag op tier ≥ 9 zonder misconception tags op afleiders',
}

// ─── Core API ─────────────────────────────────────────────────────────────────

/**
 * Aggregate guard telemetry into governance-compatible issue summaries.
 *
 * Reads all stored telemetry entries, groups rejections by question ID,
 * and produces a sorted list of problematic questions.
 *
 * @param learnerId - Optional: filter to a specific learner's sessions
 */
export function getGuardGovernanceIssues(
  learnerId?: string,
): GuardGovernanceSummary {
  const telemetry = getGuardTelemetry(learnerId)

  // Aggregate rejections by question ID
  const questionMap = new Map<
    string,
    {
      familyId: string
      fatalCount: number
      softCount: number
      reasons: Record<string, number>
      firstSeen: string
      lastSeen: string
    }
  >()

  for (const entry of telemetry.recentEntries) {
    // Prefer denormalized rejections on entry (v4.6+), fall back to stats.rejections
    const rejections: GuardRejection[] =
      entry.rejections ?? entry.stats?.rejections ?? []
    for (const rejection of rejections) {
      const existing = questionMap.get(rejection.questionId)
      if (existing) {
        if (rejection.severity === 'fatal') existing.fatalCount++
        else existing.softCount++
        existing.reasons[rejection.reason] =
          (existing.reasons[rejection.reason] ?? 0) + 1
        if (entry.timestamp > existing.lastSeen)
          existing.lastSeen = entry.timestamp
        if (entry.timestamp < existing.firstSeen)
          existing.firstSeen = entry.timestamp
      } else {
        questionMap.set(rejection.questionId, {
          familyId: rejection.familyId ?? 'unknown',
          fatalCount: rejection.severity === 'fatal' ? 1 : 0,
          softCount: rejection.severity === 'soft' ? 1 : 0,
          reasons: { [rejection.reason]: 1 },
          firstSeen: entry.timestamp,
          lastSeen: entry.timestamp,
        })
      }
    }
  }

  // Build sorted issue list
  const issues: GuardGovernanceIssue[] = []
  for (const [questionId, data] of questionMap) {
    const totalCount = data.fatalCount + data.softCount
    const primaryReason =
      Object.entries(data.reasons).sort((a, b) => b[1] - a[1])[0]?.[0] ??
      'unknown'

    issues.push({
      questionId,
      familyId: data.familyId,
      rejectionCount: totalCount,
      fatalCount: data.fatalCount,
      softCount: data.softCount,
      reasons: data.reasons,
      primaryReason,
      firstSeen: data.firstSeen,
      lastSeen: data.lastSeen,
    })
  }

  // Sort by rejection count (most problematic first)
  issues.sort((a, b) => b.rejectionCount - a.rejectionCount)

  // Build reason breakdown
  const reasonTotals: Record<string, number> = {}
  for (const issue of issues) {
    for (const [reason, count] of Object.entries(issue.reasons)) {
      reasonTotals[reason] = (reasonTotals[reason] ?? 0) + count
    }
  }
  const reasonBreakdown = Object.entries(reasonTotals)
    .map(([reason, count]) => ({
      reason,
      count,
      description: REASON_DESCRIPTIONS[reason] ?? reason,
    }))
    .sort((a, b) => b.count - a.count)

  // Data range
  const timestamps = telemetry.recentEntries.map((e) => e.timestamp)
  const dataRange =
    timestamps.length > 0
      ? {
          from: timestamps.reduce((a, b) => (a < b ? a : b)),
          to: timestamps.reduce((a, b) => (a > b ? a : b)),
        }
      : null

  return {
    totalAffectedQuestions: issues.length,
    totalRejections: issues.reduce((sum, i) => sum + i.rejectionCount, 0),
    issues,
    reasonBreakdown,
    dataRange,
  }
}
